import { FC, ReactNode, RefObject, createContext, useContext, useRef } from 'react';
import {
  useActiveUnitPathSegment,
  type UseActiveUnitPathSegmentResult,
} from '../custom-hooks/useActiveUnitPathSegment';

interface UnitPathSegmentContextValue extends UseActiveUnitPathSegmentResult {
  // Must be attached to the sticky unit header's root element: the scroll-spy measures its bottom edge
  // to place the reading line.
  headerRef: RefObject<HTMLDivElement>;
}

const UnitPathSegmentContext = createContext<UnitPathSegmentContextValue | null>(null);

/**
 * Runs the unit-path scroll-spy ONCE for the whole dashboard and shares it. Both consumers — the unit
 * header's segment indicator and the floating UnitPathRail — read the same `activeIndex`, so they can
 * never disagree about which segment the student is on. A second instance of the hook would compute its
 * own reading line and drift out of sync, which is exactly what this provider exists to prevent.
 */
export const UnitPathSegmentProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const segmentSpy = useActiveUnitPathSegment({ headerRef });

  return (
    <UnitPathSegmentContext.Provider value={{ ...segmentSpy, headerRef }}>{children}</UnitPathSegmentContext.Provider>
  );
};

export const useUnitPathSegment = (): UnitPathSegmentContextValue => {
  const value = useContext(UnitPathSegmentContext);
  if (!value) {
    throw new Error('useUnitPathSegment must be used inside a UnitPathSegmentProvider');
  }
  return value;
};
